import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { IFeatureGroupRouterService } from "../interfaces/feature-group-router-service";
import { AbstractRouterService } from "./abstract-router.service";
import { NavigationSource } from "../enums/navigation-source";

/**
 * The abstract class that all RouterServices extend from
 *
 * A RouterService should be created for each RoutingModule.
 * It is the way that Components, Directives and Services will interact with the router
 *
 * @see IFeatureGroupRouterService
 * @see IRouterService
 * @see IRouteData
 * @see MgdRouterService
 */
@Injectable()
export abstract class AbstractFeatureGroupRouterService<R extends string> extends AbstractRouterService<R>
    implements IFeatureGroupRouterService {

    /**
     * Default constructor featureGroupId is required
     */
    constructor(featureGroupId: string) {
        super(featureGroupId);
    }

    /**
     * Exit the module and back to the parent module
     */
    exit(): void {

        const active = this._mgdRouter.activeRoute;
        if (active) {
            this._mgdRouter.navigate([active.exitPath], NavigationSource.RouterService, {
                relativeToCurrentRoute: true
            });
        }
    }

    /**
     * Emits when this feature group deactivates (user navigates away).
     * Use with `takeUntilDestroyed` in workflow services for reset-on-exit.
     *
     * @example
     * ```typescript
     * this.router.onExit$().pipe(
     *     takeUntilDestroyed(this._destroyRef)
     * ).subscribe(() => this._reset());
     * ```
     */
    onExit$(): Observable<void> {
        return this._mgdRouter.onFeatureGroupExit$(this.featureGroupId);
    }

    /**
     * Returns true if the route's routeId matches the provided routeId
     */
    protected _checkRouteId(routeId: string): boolean {

        return this._mgdRouter.isActiveRouteId(this.featureGroupId, routeId);
    }
}
